import { trackEvent } from "@features/tracking/tracking"
import { parseDollars } from "@utils/money-format"
import { safeStorage } from "@utils/safeStorage"
import { proxy, subscribe } from "valtio"
import { FerveAPI } from "./ferve-api-client"
import { formatFerveDate } from "@utils/date-format"

export type FerveCartItem = {
  key: string
  hash: string
  title: string
  price: number
  priceTypeLabel: string
  total: number
  dateText?: string
  dateTime?: string
  ferveDate?: string
  venueText?: string
  quantity: number
  quantityOptions: string[]
  questionAnswers: []
  children: FerveCartItem[]
  seats?: string[]
  isChild?: boolean
  isDonation?: boolean
  isCartDonation?: boolean
  isFlexipass?: boolean
  isGiftCard?: boolean
  isMerchandise?: boolean
  enableAcknowledgement?: boolean
}

export type FerveCartData = {
  totalValue: number
  totalValueWithoutCartDonation: number
  fees: number
  totalTickets: number
  isEmpty: boolean
  items: FerveCartItem[]
  shouldPromptCartDonation?: boolean
  potentialRoundUpAmount?: number
  /** The state of the in-cart donation, mostly for tracking purposes */
  inCartDonation?: CartDonationPrefs
}

export type CartDonationPrefs = {
  mode: "no-thanks" | "roundup" | "preset" | "other" | "n/a"
  amount?: number
}

/**
 * Used for passes/donations etc
 */
export type ItemPricing = {
  label: string
  price: number
  itemHash: string
  priceTypeHash: string
  conditionsText?: string
  codeHash?: string
  options: string[]
  isDonation?: boolean
}

const CART_DATA_VERSION = 1

export class FerveCart {
  public state = proxy({
    version: CART_DATA_VERSION,
    hashes: new Set<string>(),
    loading: false,
    error: null as null | string,
    data: null as null | FerveCartData,
    donationPrefs: {
      mode: "roundup",
    } as CartDonationPrefs,
    expiryTime: null as null | number,
    showingExpiryCountdown: false,
    showingExpiredNotice: false,
    dismissedExpiryCountdown: false,
  })

  // Full amount of time that items can be in cart while inactive
  CART_EXPIRY_AGE = 20 * 60 * 1000

  // Amount of warning to give users before removing from cart
  // This will be shown at cartExpiryAge - cartExpiryCountdown
  CART_EXPIRY_COUNTDOWN = 5 * 60 * 1000

  constructor(public api: FerveAPI) {
    if (env.client) {
      // Initalize after a moment
      setTimeout(async () => {
        this.updateFromStorage()
        await this.checkExpiryTime()
        this.refreshCart()
      })

      // Listen for localStorage changes
      api.onDestroy(safeStorage.subscribe([this.getStorageKey()], () => this.updateFromStorage()))

      // Save to localStorage when the state changes
      api.onDestroy(subscribe(this.state, () => this.saveToStorage(), false))

      // Check for expiry time every second
      const timer = setInterval(() => {
        this.checkExpiryTime()
      }, 1000)
      api.onDestroy(() => {
        clearInterval(timer)
      })
    }
  }

  private getStorageKey() {
    return "saved_cart:" + this.api.mode
  }

  public saveToStorage() {
    safeStorage.setItem(this.getStorageKey(), JSON.stringify({ ...this.state, version: CART_DATA_VERSION }))
  }

  public updateFromStorage() {
    try {
      const hashes = safeStorage.getItem(this.getStorageKey())
      if (!hashes) return
      const data = JSON.parse(hashes)
      if (data && data.version === CART_DATA_VERSION) {
        Object.assign(this.state, data)
      }
    } catch (e) {}
  }

  bumpCartExpiry() {
    if (this.state.data?.isEmpty) {
      this.state.expiryTime = null
    } else {
      this.state.expiryTime = Date.now() + this.CART_EXPIRY_AGE
    }
    this.state.dismissedExpiryCountdown = false
    this.state.showingExpiredNotice = false
    this.state.showingExpiryCountdown = false
  }

  async checkExpiryTime() {
    if (!this.state.expiryTime) {
      return
    }

    // If the expiry time has come and gone, expire the cart now, and display a message
    const mustExpire = this.state.expiryTime < Date.now()
    if (mustExpire) {
      trackEvent(
        "Shown Cart Expired Alert",
        {
          MinutesAgo: Math.floor((Date.now() - this.state.expiryTime) / (60 * 1000)),
        },
        null,
      )
      this.state.expiryTime = null
      this.state.showingExpiredNotice = true
      this.state.showingExpiryCountdown = false
      this.state.dismissedExpiryCountdown = false
      await this.clearCart()
      return
    }

    // If the expiry time is within the countdown, show a warning
    const showCountown = this.state.expiryTime - this.CART_EXPIRY_COUNTDOWN < Date.now()
    if (showCountown && !this.state.dismissedExpiryCountdown) {
      if (!this.state.showingExpiryCountdown) {
        trackEvent("Shown Cart Expires Soon Alert", {}, null)
      }
      this.state.showingExpiryCountdown = true
      return
    }
  }

  async clearCart() {
    await this.refreshCart()
  }

  async remove(cartItemHash: string | string[], refresh = true, debug?: string) {
    this.bumpCartExpiry()
    const items = Array.isArray(cartItemHash) ? cartItemHash : [cartItemHash]
    for (const hash of items) {
      await this.api.GET("Cart/Remove", { cartItemHash: hash, source: 2, debug: debug + "-2" })
    }
    if (refresh) {
      await this.refreshCart()
    }
    this.bumpCartExpiry()
  }

  async updateQuantity(cartItemHash: string, quantity: number, refresh = true) {
    const result = await this.api.GET("Cart/Update", { cartItemHash, quantity, source: "ft_ferveCartView" })
    if (refresh) {
      this.handleCartResult(result)
    }
    this.bumpCartExpiry()
  }

  async addItemsToCart(
    itemHash: string,
    selection: { quantity: number; type: { priceTypeHash?: string; codeHash?: string } }[],
    refresh = true,
  ) {
    const items = []
    for (let item of selection) {
      if (item.quantity > 0) {
        items.push({
          ItemHash: itemHash,
          Quantity: String(item.quantity),
          PriceTypeHash: item.type.priceTypeHash,
          CodeHash: item.type.codeHash || null,
          SeatingPlanSection: "",
          SeatSource: 0,
          Allocated: false,
          Split: false,
          GeneralAdmission: false,
          Holds: null,
          QuestionAnswersToAdd: [],
        })
      }
    }
    const result = await this.api.POST("Cart/Add", items)
    if (result.Errors.length) {
      throw new Error(result.Errors[0].CustomerMessage)
    }
    if (refresh) {
      await this.refreshCart()
    }
    this.bumpCartExpiry()
    return result
  }

  async getPricing(itemHash: string, dateHash?: string) {
    const lookup = await this.api.GET("Pricing/List", {
      itemHash,
      dateHash,
      app: false,
    })
    if (lookup.Errors?.length) {
      throw new Error(lookup.Errors[0].CustomerMessage)
    }
    if (!lookup.Success) {
      throw new Error("Unable to validate code")
    }
    const pricing: ItemPricing[] = []
    if (lookup.Success && lookup.SuccessMessages) {
      const $contents = document.createElement("div")
      $contents.innerHTML = lookup.SuccessMessages[0]?.replace(/<img[^>]+>/g, "")

      $contents.querySelectorAll("img,script").forEach(($img) => {
        $img.remove()
      })

      $contents.querySelectorAll(".ft_ep_priceRow").forEach(($row) => {
        const quantitySelector = $row.querySelector("select[name=qty]")
        const item: ItemPricing = {
          label: $row.querySelector(".ft_ep_type")?.textContent!,
          price: parseDollars($row.querySelector(".ft_ep_price")?.textContent?.replace(/[^\$0-9.,]/g, "") ?? ""),
          priceTypeHash: quantitySelector?.getAttribute("data-pricetypehash")!,
          itemHash: itemHash!,
          codeHash: quantitySelector?.getAttribute("data-codehash")!,
          conditionsText: $row.querySelector(".ft_ep_conditionsRow")?.textContent!,
          options: [],
        }
        quantitySelector?.querySelectorAll("option").forEach((option) => {
          item.options.push(option.textContent!)
        })
        pricing.push(item)
      })

      $contents.querySelectorAll("input[name=qty]").forEach(($input) => {
        const item: ItemPricing = {
          label: $contents.querySelector(`label[for=${$input.id}`)?.textContent!,
          price: parseFloat(($input as HTMLInputElement).value),
          priceTypeHash: $input.getAttribute("data-pricetypehash")!,
          itemHash: itemHash!,
          codeHash: undefined,
          options: [],
          isDonation: true,
        }

        pricing.push(item)
      })
    }
    return pricing!
  }

  async refreshCart() {
    this.state.loading = true
    const result = await this.api.GET("Cart/View")

    // Handle direct errors from Ferve
    if (result.Errors?.length) {
      let error = result.Errors[0].CustomerMessage || "An unknown error occurred while refreshing your cart"
      if (error === "Invalid Hash.") {
        // This should never happen, but just in case, show a nicer error
        error = "Your session has expired. Please refresh the page."
      }
      this.state.loading = false
      return
    }

    this.handleCartResult(result)
  }

  private async handleCartResult(result: any) {
    const cart: FerveCartData = {
      items: [],
      totalValue: 0,
      totalValueWithoutCartDonation: 0,
      fees: 0,
      isEmpty: true,
      totalTickets: 0,
    }

    const successData = result.SuccessMessages
    cart.totalValue = parseFloat(successData[2].replace(/,/g, ""))
    cart.totalValueWithoutCartDonation = cart.totalValue

    const $contents = document.createElement("div")
    $contents.innerHTML = successData[3]?.replace(/<img[^>]+>/g, "") ?? ""

    $contents.querySelectorAll("img,script").forEach(($img) => {
      $img.remove()
    })

    $contents.querySelectorAll("[id=ft_fcFees]").forEach(($span, k) => {
      const matches = $span.innerHTML.match(/\$[0-9\.,]+/g)
      if (matches) {
        for (let match of matches) {
          cart.fees += parseDollars(match)
        }
      }
    })

    $contents.querySelectorAll(".ft_fcItem").forEach(($item, i) => {
      const $ = (selector: string) => $item.querySelector(selector)

      const quantitySelector = $(".ft_fcItemQtySelect") as HTMLSelectElement | null

      const item: FerveCartItem = {
        key: "",
        hash: ($(".ft_fcItemRemove")?.innerHTML ?? "")?.match(/[a-f0-9]{32}/i)?.[0] ?? "",
        title: $(".ft_fcItemTitle a")?.textContent ?? "",
        price: parseDollars($(".ft_fcItemPrice")?.textContent ?? ""),
        priceTypeLabel:
          $(".ft_fcItemType")
            ?.innerHTML?.replace(/<.+$/m, "")
            ?.replace(/\s-\sRow.+$/, "") ?? "",
        total: parseDollars($(".ft_fcItemTotal")?.textContent ?? ""),
        dateText: ($(".ft_fcItemDetails")?.textContent || "").match(/[A-z0-9 :,]+/)?.[0]?.trim(),
        venueText: $(".ft_fcItemDetails a")?.textContent || undefined,
        quantity: quantitySelector
          ? parseInt(quantitySelector.querySelector("option[selected]")?.getAttribute("value") ?? "") || 1
          : parseInt($(".ft_fcItemQtyText")?.textContent ?? "") || 1,
        quantityOptions: quantitySelector
          ? Array.from(quantitySelector.querySelectorAll("option")).map((o) => o.value)
          : [],
        questionAnswers: [],
        children: [],
        seats: $(".ft_fcItemType")
          ?.textContent?.match(/Row\ [A-Z]+\ Seats?\ [0-9\,\s]+/gi)
          ?.filter((item) => item.length >= 2),
      }

      if (item.dateText) {
        const date = new Date(item.dateText)
        item.dateTime = date.toUTCString()
        item.ferveDate = formatFerveDate(item.dateTime)
      }

      item.key = `${item.title} ${item.dateText} ${item.venueText}`

      if (item.title.match(/donation|admit\sall\sprogram|patron\sprogram|film\sfor\sall/i)) {
        item.isDonation = true
      }
      // TODO: Use ID from config
      if (item.title.includes("In-Cart Donation")) {
        item.isCartDonation = true
        item.isDonation = false
      }

      if ($(".ft_fcItemTitle a")?.getAttribute("href")?.includes("Merchandise")) {
        item.isMerchandise = true
      }

      if (item.isDonation && item.total > 100) {
        item.enableAcknowledgement = true
      }

      if (item.title.match(/(flexipass|uni pass|youth pass)/i)) {
        item.isFlexipass = true
      }

      // TODO: Gift Cards
      if (item.title.match(/(gift voucher|gift card)/i)) {
        item.isGiftCard = true
      }

      if (!item.isCartDonation) {
        cart.isEmpty = false
      }

      if ($item.classList.contains("ft_fcChild") || ($item.classList.contains("ft_fcChild") && cart.items.length > 0)) {
        item.isChild = true
        cart.items[cart.items.length - 1].children.push(item)
      } else {
        cart.items.push(item)
      }
    })

    cart.totalTickets = cart.items.reduce(
      (acc, item) => acc + (item.isDonation || isNaN(item.quantity) ? 0 : item.quantity),
      0,
    )

    cart.shouldPromptCartDonation = true
    // Don't prompt for a donation if the user has added one manually
    if (cart.items.some((item) => item.isDonation)) {
      cart.shouldPromptCartDonation = false
    }

    // cart.shouldPromptCartDonation = false

    cart.potentialRoundUpAmount = 0
    if (cart.shouldPromptCartDonation) {
      const currentDonationAmount = cart.items
        .filter((item) => item.isCartDonation)
        .reduce((acc, item) => acc + item.total, 0)
      const totalWithoutDonation = cart.totalValue - currentDonationAmount
      cart.potentialRoundUpAmount = this.calculateRoundupAmount(totalWithoutDonation)
      if (cart.potentialRoundUpAmount === 0) {
        cart.shouldPromptCartDonation = false
      }
    }

    const cartWasRefreshed = await this.handleInCartDonation(cart)
    if (cartWasRefreshed) return

    // if (cart.isEmpty) {
    //   this.state.expiryTime = null
    // } else if (!this.state.expiryTime) {
    //   this.bumpCartExpiry()
    // }

    if (!cart.shouldPromptCartDonation) {
      this.state.donationPrefs = {
        mode: "roundup",
      }
    }

    this.state.data = cart
    this.state.loading = false
    this.state.error = null
    this.state.hashes = new Set(cart.items.map((item) => item.hash).filter(Boolean))
  }

  private calculateRoundupAmount(cartTotal: number) {
    if (cartTotal <= 5) return 0
    const min = 2
    const nearest = 5
    return Math.ceil((cartTotal + min) / nearest) * nearest - cartTotal
  }

  get inCartDonationsEnabled() {
    return !!this.api.conn.inCartDonations
  }

  private async handleInCartDonation(cart: FerveCartData) {
    if (!this.inCartDonationsEnabled) {
      return false
    }
    const currentDonation = cart.items.filter((item) => item.isCartDonation)
    const pref = this.state.donationPrefs

    let currentValue = currentDonation.reduce((acc, item) => acc + item.total, 0)
    let targetValue = 0
    if (cart.shouldPromptCartDonation) {
      if (pref.mode === "roundup") {
        targetValue = cart.potentialRoundUpAmount || 0
      } else if (pref.mode === "other" || pref.mode === "preset") {
        targetValue = pref.amount || 0
      }
    }

    targetValue = Math.round(targetValue * 100) / 100
    currentValue = Math.round(currentValue * 100) / 100

    /** Track the actual state, mostly for tracking purposes */
    cart.inCartDonation = {
      mode: "n/a",
      amount: 0,
    }

    if (cart.shouldPromptCartDonation) {
      cart.inCartDonation = pref
      // if (pref.mode === "no-thanks") {
      //   cart.inCartDonation.mode = "no-thanks"
      // } else if (pref.mode === "custom") {
      //   if (pref.mode === "custom" && pref.)
      //   cart.inCartDonation.mode = "other"
      //   cart.inCartDonation.amount = targetValue
      // } else if (pref.mode === "roundup") {
      //   cart.inCartDonation.mode = "roundup"
      //   cart.inCartDonation.amount = targetValue
      // }
    }

    // If the target donation is the same as the current donation, do nothing.
    if (Math.abs(currentValue * 100 - targetValue * 100) <= 2) {
      return
    }

    // console.log("Target", Math.ceil(targetValue * 100))
    const ferveQuantity = Math.round(targetValue * 100)

    // Remove all existing in-cart donations
    if (currentDonation.length > 0) {
      // console.log("Removing in-cart donation", currentDonation, "because", currentValue, "!==", targetValue)
      // console.log("Current items are", JSON.parse(JSON.stringify(cart.items)))
      // console.log(
      //   "Removing in-cart donation with hashes",
      //   currentDonation.map((item) => item.hash),
      //   "because current value",
      //   currentValue,
      //   "!== target value",
      //   targetValue,
      // )

      // return true
      await this.remove(
        currentDonation.map((item) => item.hash),
        false,
        "in-cart-donation",
      ).catch((e) => {
        console.warn("Failed to remove in-cart donation", e)
      })
    }

    // Add the donation
    if (targetValue > 0) {
      let itemHash = "27de88cceae8456e8ecd8288ca226c88"
      let priceTypeHash = "158ea6e5c88c4f32b8dfd8681d429c47"
      if (this.api.mode === "tff") {
        itemHash = "3f2a56202e8d4d628f47e59bf9f55507"
        priceTypeHash = "3c13e279aa554e35b7f744b322e680f6"
      }
      await this.addItemsToCart(
        itemHash,
        [
          {
            quantity: ferveQuantity,
            type: {
              priceTypeHash: priceTypeHash,
            },
          },
        ],
        true,
      )
      return true
    }

    await this.refreshCart()
    return true
  }

  setDonationPrefs(prefs: CartDonationPrefs) {
    this.state.donationPrefs = {
      ...this.state.donationPrefs,
      ...prefs,
    }
    this.refreshCart()
  }
}
